import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DynamicLogo from './DynamicLogo';
import { getActiveAuthRole } from '../utils/authStorage';
import { 
  FaDesktop, 
  FaCreditCard,
  FaBroadcastTower, 
  FaLink, 
  FaChartBar, 
  FaExchangeAlt, 
  FaUsers,
  FaStore,
  FaCog,
  FaTags
} from 'react-icons/fa';

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const activePath = location.pathname;
  const [isOpen, setIsOpen] = useState(() => {
    try {
      return sessionStorage.getItem('ecr-sidebar-open') === '1';
    } catch {
      return false;
    }
  });
  const [storageEpoch, setStorageEpoch] = useState(0);
  const keepOpenRef = useRef(false);

  const setSidebarOpen = (open) => {
    setIsOpen(open);
    try {
      sessionStorage.setItem('ecr-sidebar-open', open ? '1' : '0');
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'token' || e.key === 'user' || e.key === 'role' || e.key === 'Role' || e.key === null) {
        setStorageEpoch((n) => n + 1);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const userRole = useMemo(() => getActiveAuthRole(), [activePath, storageEpoch]);

  let menuItems = [];

  // 🎯 Cas Marketing → juste Screen saver
  if (userRole === 'marketing') {
    menuItems = [
      { path: '/tags', label: 'Tags', icon: FaTags },
      { path: '/regit', label: 'Screen saver', icon: FaBroadcastTower }
    ];
  } 
  // 🎯 Cas Admin
  else if (userRole === 'admin') {
    menuItems = [
      { path: '/users', label: 'Utilisateurs', icon: FaUsers },
      { path: '/magasins', label: 'Magasins', icon: FaStore },
      { path: '/transaction', label: 'Transactions', icon: FaExchangeAlt },
      { path: '/pair', label: 'Pairage', icon: FaLink },
      { path: '/devices', label: 'Caisses', icon: FaDesktop },
      { path: '/devicestpe', label: 'TPE', icon: FaCreditCard },
      { path: '/dashboard', label: 'Analytics', icon: FaChartBar },
      { path: '/regit', label: 'Screen Saver', icon: FaBroadcastTower },
      { path: '/tags', label: 'Tags', icon: FaTags },
      { path: '/configuration', label: 'Configuration', icon: FaCog },
    ];
  } 
  // 🎯 Cas Manager
  else if (userRole === 'manager') {
    menuItems = [
      { path: '/transaction', label: 'Transactions', icon: FaExchangeAlt },
      { path: '/devices', label: 'Caisses', icon: FaDesktop },
      { path: '/devicestpe', label: 'TPE', icon: FaCreditCard },
      { path: '/dashboard', label: 'Analytics', icon: FaChartBar },
    ];
  } 
  else if (userRole === 'finance') {
    menuItems = [
      { path: '/transaction', label: 'Transactions', icon: FaExchangeAlt },
      { path: '/devicestpe', label: 'TPE', icon: FaCreditCard },
    ];
  }
  else if (userRole === 'merchant') {
    menuItems = [
      { path: '/dashboard', label: 'Analytics', icon: FaChartBar },
      { path: '/magasins', label: 'Magasins', icon: FaStore },
      { path: '/transaction', label: 'Transactions', icon: FaExchangeAlt },
      { path: '/devices', label: 'Caisses', icon: FaDesktop },
      { path: '/devicestpe', label: 'TPE', icon: FaCreditCard },
    ];
  }
  // 🎯 Cas autres rôles → menu par défaut
  else {
    menuItems = [
      { path: '/transaction', label: 'Transactions', icon: FaExchangeAlt },
      { path: '/devices', label: 'Caisses', icon: FaDesktop },
      { path: '/devicestpe', label: 'TPE', icon: FaCreditCard },
      { path: '/dashboard', label: 'Analytics', icon: FaChartBar },

    ];
  }

  return (
    <div
      className={`relative h-screen overflow-hidden transition-all duration-300 ease-in-out ${
        isOpen ? 'w-60' : 'w-[4.5rem]'
      }`}
      style={{ backgroundColor: '#12182B' }}
      onMouseEnter={() => {
        keepOpenRef.current = false;
        setSidebarOpen(true);
      }}
      onMouseLeave={() => {
        if (keepOpenRef.current) return;
        setSidebarOpen(false);
      }}
    >
      <div className="flex h-full flex-col overflow-hidden">
        <div className={`flex shrink-0 items-center justify-center ${isOpen ? 'px-3 py-4' : 'px-2 py-3'}`}>
          <DynamicLogo className={`${isOpen ? 'h-9' : 'h-7'} object-contain`} />
        </div>

        <nav className="flex min-h-0 flex-1 flex-col overflow-hidden px-2 py-1">
          <div className="flex min-h-0 flex-col gap-1.5 overflow-hidden">
            {menuItems.map(({ path, label, icon: Icon }) => {
              const isActive = activePath === path;
              return (
                <button
                  key={path}
                  type="button"
                  onClick={() => {
                    keepOpenRef.current = true;
                    setSidebarOpen(true);
                    navigate(path);
                  }}
                  className={`group flex w-full items-center overflow-hidden rounded-full transition-colors duration-150
                    ${isOpen ? 'gap-3 py-1 pl-1 pr-3' : 'justify-center py-1'}
                    ${isActive ? 'text-white' : 'hover:bg-white/5'}`}
                  style={isActive ? { backgroundColor: '#1c2a6d' } : undefined}
                >
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full
                      ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-white'}`}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <span
                    className={`truncate text-sm font-medium whitespace-nowrap
                      ${isOpen ? 'opacity-100' : 'w-0 overflow-hidden opacity-0'}
                      ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-white'}`}
                  >
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>
      </div>
    </div>
  );
}